"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PieceDragContextProvider = PieceDragContextProvider;
exports.useActiveDrag = useActiveDrag;
exports.useDragPiece = useDragPiece;
exports.useDropSurface = useDropSurface;
const React = __importStar(require("react"));
const ActionsContext = React.createContext(null);
const ActiveDragContext = React.createContext(null);
const NOOP_ACTIONS = {
    register: () => () => undefined,
    drop: () => false,
    startDrag: () => undefined,
    moveDrag: () => undefined,
    endDrag: () => undefined,
};
function PieceDragContextProvider({ children, }) {
    const surfaces = React.useRef(new Set());
    const [activeDrag, setActiveDrag] = React.useState(null);
    const actions = React.useMemo(() => ({
        register: (surface) => {
            surfaces.current.add(surface);
            return () => {
                surfaces.current.delete(surface);
            };
        },
        drop: (id, clientX, clientY) => {
            for (const surface of surfaces.current) {
                const rect = surface.el.getBoundingClientRect();
                if (clientX < rect.left ||
                    clientX >= rect.right ||
                    clientY < rect.top ||
                    clientY >= rect.bottom) {
                    continue;
                }
                const x = Math.floor(((clientX - rect.left) / rect.width) * surface.width);
                const y = Math.floor(((clientY - rect.top) / rect.height) * surface.height);
                if (!surface.canDrop(id, x, y)) {
                    return false;
                }
                surface.onDrop(id, x, y);
                return true;
            }
            return false;
        },
        startDrag: (drag) => setActiveDrag(drag),
        moveDrag: (clientX, clientY) => setActiveDrag((prev) => (prev ? Object.assign(Object.assign({}, prev), { clientX, clientY }) : prev)),
        endDrag: () => setActiveDrag(null),
    }), []);
    return (React.createElement(ActionsContext.Provider, { value: actions },
        React.createElement(ActiveDragContext.Provider, { value: activeDrag }, children)));
}
function useActions() {
    var _a;
    return (_a = React.useContext(ActionsContext)) !== null && _a !== void 0 ? _a : NOOP_ACTIONS;
}
function useActiveDrag() {
    return React.useContext(ActiveDragContext);
}
const DRAG_THRESHOLD_PX = 4;
/**
 * Attach the returned handlers to a draggable piece element.
 * Capture is acquired only once a drag actually starts (threshold crossed),
 * so short taps/clicks still bubble naturally.
 */
function useDragPiece(id) {
    const { drop, startDrag, moveDrag, endDrag } = useActions();
    const state = React.useRef(null);
    const onPointerDown = React.useCallback((e) => {
        if (e.pointerType === "mouse" && e.button !== 0) {
            return;
        }
        state.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            dragging: false,
        };
    }, []);
    const onPointerMove = React.useCallback((e) => {
        if (!state.current) {
            return;
        }
        if (!state.current.dragging) {
            const dx = e.clientX - state.current.startX;
            const dy = e.clientY - state.current.startY;
            if (dx * dx + dy * dy <= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
                return;
            }
            state.current.dragging = true;
            // capture now so pointermove/up keep firing on this element
            // even when the pointer leaves the piece
            try {
                e.currentTarget.setPointerCapture(state.current.pointerId);
            }
            catch (_a) {
                // ignore
            }
            const rect = e.currentTarget.getBoundingClientRect();
            startDrag({
                id,
                clientX: e.clientX,
                clientY: e.clientY,
                width: rect.width,
                height: rect.height,
                offsetX: e.clientX - rect.left,
                offsetY: e.clientY - rect.top,
            });
            return;
        }
        moveDrag(e.clientX, e.clientY);
    }, [id, startDrag, moveDrag]);
    const onPointerUp = React.useCallback((e) => {
        const s = state.current;
        state.current = null;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch (_a) {
            // ignore
        }
        if (!s || !s.dragging) {
            return;
        }
        endDrag();
        drop(id, e.clientX, e.clientY);
        // suppress the synthetic click that would otherwise fire after pointerup
        suppressNextClick();
    }, [id, drop, endDrag]);
    const onPointerCancel = React.useCallback((e) => {
        const s = state.current;
        state.current = null;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch (_a) {
            // ignore
        }
        if (s === null || s === void 0 ? void 0 : s.dragging) {
            endDrag();
        }
    }, [endDrag]);
    return React.useMemo(() => ({ onPointerDown, onPointerMove, onPointerUp, onPointerCancel }), [onPointerDown, onPointerMove, onPointerUp, onPointerCancel]);
}
function suppressNextClick() {
    const handler = (e) => {
        e.stopPropagation();
        e.preventDefault();
        cleanup();
    };
    const cleanup = () => {
        window.removeEventListener("click", handler, true);
        window.clearTimeout(timeout);
    };
    const timeout = window.setTimeout(cleanup, 100);
    window.addEventListener("click", handler, true);
}
/**
 * Register an element as a drop surface. The surface is treated as a
 * `width * height` grid; the target tile is computed from the pointer
 * position relative to the element's bounding rect.
 */
function useDropSurface(ref, width, height, onDrop, canDrop) {
    const { register } = useActions();
    // keep latest callbacks without re-registering
    const latest = React.useRef({ onDrop, canDrop, width, height });
    latest.current = { onDrop, canDrop, width, height };
    React.useEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        return register({
            el,
            get width() {
                return latest.current.width;
            },
            get height() {
                return latest.current.height;
            },
            onDrop: (id, x, y) => latest.current.onDrop(id, x, y),
            canDrop: (id, x, y) => latest.current.canDrop(id, x, y),
        });
    }, [ref, register]);
}
